"use client";

import { useState, useEffect } from "react";
import { useLanguage } from "../../hooks/useLanguage";
import config from "../../config/config";
import Link from "next/link";

export default function QuickMenuSection() {
  const [quickMenuData, setQuickMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuVisible, setMenuVisible] = useState(false); // Single state for both menus
  const { language } = useLanguage();

  // Helper function to get localized text
  const getLocalizedText = (textObj) => {
    if (!textObj) return '';
    if (typeof textObj === 'string') return textObj;
    return textObj[language] || textObj.en || textObj.hi || textObj.mr || '';
  };

  // Fetch quick menu settings from API
  useEffect(() => {
    const fetchQuickMenuSettings = async () => {
      try {
        const response = await fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/homepage/quick-menu`);
        const data = await response.json();
        console.log('Quick Menu API Response:', data); // Debug log
        
        if (data.success && data.data) {
          console.log('Quick Menu Data:', data.data); // Debug log
          setQuickMenuData(data.data);
        }
      } catch (error) {
        console.error('Error fetching quick menu settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchQuickMenuSettings();
  }, [language]);

  // Simple scroll handler - show menu after 10% scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercentage = (currentScrollY / documentHeight) * 100;
      
      // Show menu only after 10% scroll, then keep it visible
      if (scrollPercentage >= 10) {
        setMenuVisible(true);
      } else {
        setMenuVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Don't render if disabled or no data
  if (loading) {
    return (
      <section className="py-6 sm:py-8 md:py-10 lg:py-12 xl:py-14 px-2 sm:px-3 md:px-4 lg:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded-lg h-24 w-full"></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Don't render if disabled, no data, or enabled is explicitly false
  if (!quickMenuData || quickMenuData.enabled === false || !quickMenuData?.menuItems?.length) {
    console.log('Quick Menu hidden:', { 
      hasData: !!quickMenuData,
      enabled: quickMenuData?.enabled,
      itemsLength: quickMenuData?.menuItems?.length 
    });
    return null;
  }

  console.log('Quick Menu will render:', {
    enabled: quickMenuData.enabled,
    itemsCount: quickMenuData.menuItems.length
  });

  // Filter active menu items
  const activeMenuItems = quickMenuData.menuItems.filter(item => item.isActive !== false);

  if (activeMenuItems.length === 0) {
    return null;
  }

  return (
    <>
      {/* Desktop Floating Left Sidebar - Vertical Menu */}
      <div className={`hidden md:block fixed left-4 top-1/2 transform -translate-y-1/2 z-30 transition-all duration-300 ease-in-out ${
        menuVisible ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
      }`}>
        <div className="bg-card/95 backdrop-blur-md border border-border rounded-xl shadow-lg p-1 max-w-[120px]">
          {/* Vertical Menu Items */}
          <div className="flex flex-col space-y-1">
            {activeMenuItems.slice(0, 4).map((item, index) => (
              <Link
                key={item.id || index}
                href={item.url || '#'}
                target={item.target || '_self'}
                className="group block"
              >
                <div className="bg-card border border-border rounded-lg p-1 hover:shadow-md hover:border-primary/50 transition-all duration-300 transform hover:scale-105 text-center">
                  {/* Image - Smaller size */}
                  {item.image && (
                    <div className="relative w-6 h-6 mb-1 overflow-hidden rounded-md mx-auto flex-shrink-0">
                      <img
                        src={item.image}
                        alt={getLocalizedText(item.title) || 'Menu Item'}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                  )}
                  
                  {/* Title - Smaller text */}
                  <h3 className="text-[10px] font-medium text-foreground text-center group-hover:text-primary transition-colors duration-300 line-clamp-2 leading-tight">
                    {getLocalizedText(item.title) || 'Menu Item'}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Bottom Sticky Version - Only on small screens */}
      <div className={`md:hidden fixed bottom-0 left-0 right-0 z-40 backdrop-blur-md shadow-lg border-t transition-all duration-300 ease-in-out ${
        menuVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`} style={{
        background: 'var(--color-card)',
        borderColor: 'var(--color-border)'
      }}>
        <div className="flex justify-center items-center px-3 py-1">
          <div className="flex items-center justify-center space-x-1 w-full max-w-sm">
            {activeMenuItems.slice(0, 4).map((item, index) => (
              <Link
                key={`mobile-${item.id || index}`}
                href={item.url || '#'}
                target={item.target || '_self'}
                className="flex-1 flex flex-col items-center justify-center py-1 group active:scale-95 transition-transform duration-200"
              >
                {/* Fixed Mobile Image Size - No background */}
                {item.image && (
                  <div className="relative w-8 h-8 mb-1 overflow-hidden rounded-lg flex-shrink-0">
                    <img
                      src={item.image}
                      alt={getLocalizedText(item.title) || 'Menu Item'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                {/* Mobile Title - Single line only */}
                <span className="text-[9px] leading-tight font-medium text-center group-active:text-primary transition-colors duration-200 line-clamp-1 px-1 h-3 flex items-center" style={{
                  color: 'var(--color-card-foreground)'
                }}>
                  {getLocalizedText(item.title) || 'Menu'}
                </span>
              </Link>
            ))}
          </div>
        </div>
        
        {/* Mobile bottom padding for safe area */}
        <div className="h-safe-area-inset-bottom"></div>
      </div>
    </>
  );
}